import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const ADMIN_SESSION_KEY = 'vf_admin_session';
const ADMIN_ID = 'veeflix_admin';
const ADMIN_PASS = 'vfadmin008';

const checkAdminSession = () => {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(ADMIN_SESSION_KEY) === '1';
};

const ALBUM_OPTIONS = [
  { slug: 'sushflix', title: 'SuShflix' },
];

function generateSlug(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function formatFileName(slug, index, extension) {
  return `${slug}-${String(index).padStart(3, '0')}.${extension}`;
}

function getBaseName(filename) {
  const parts = filename.split('.');
  if (parts.length <= 1) return filename;
  parts.pop();
  return parts.join('.');
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[AdminMediaUpload] Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main style={{ minHeight: '100vh', background: '#111', color: '#fff', padding: '2rem' }}>
          <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', borderRadius: 8, padding: '1.5rem', maxWidth: 600, margin: '0 auto' }}>
            <h2 style={{ color: '#fca5a5', marginBottom: '1rem' }}>⚠️ Error Loading Media Upload</h2>
            <p style={{ color: '#fecaca', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '0.5rem 1rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              Reload Page
            </button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

export function AdminMediaUpload() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [step, setStep] = useState(1);
  const [albumSlug, setAlbumSlug] = useState('');
  const [categoryTitle, setCategoryTitle] = useState('');
  const [categorySlug, setCategorySlug] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [skipUpload, setSkipUpload] = useState(false);

  const [categoryMeta, setCategoryMeta] = useState({
    cast: '',
    genre: '',
    occasion: '',
    tags: '',
  });

  const [itemsMeta, setItemsMeta] = useState({});

  useEffect(() => {
    try {
      const isAuth = checkAdminSession();
      if (isAuth) {
        setIsAuthenticated(true);
      } else {
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      console.error('[AdminMediaUpload] Auth check failed:', err);
      setError(err.message);
      navigate('/admin', { replace: true });
    }
    setLoading(false);
  }, [navigate]);

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    }
    navigate('/admin', { replace: true });
  };

  const handleFileSelect = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files);
    const fileData = selectedFiles.map((file, index) => ({
      id: `temp-${Date.now()}-${index}`,
      file,
      name: file.name,
      type: file.type.startsWith('video/') ? 'video' : 'photo',
      size: file.size,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      title: getBaseName(file.name),
      description: '',
    }));
    setFiles(prev => [...prev, ...fileData]);
  }, []);

  const handleReorder = (fromIndex, toIndex) => {
    const newFiles = [...files];
    const [moved] = newFiles.splice(fromIndex, 1);
    newFiles.splice(toIndex, 0, moved);
    setFiles(newFiles);
  };

  const handleItemMetaChange = (itemId, field, value) => {
    setItemsMeta(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [field]: value },
    }));
  };

  const handleCategorySlugChange = (value) => {
    setCategorySlug(generateSlug(value));
  };

  const handleUploadToCloudinary = async () => {
    if (!categorySlug || files.length === 0) return;
    
    setUploading(true);
    setUploadError('');
    setUploadedUrls([]);
    
    const uploaded = [];
    const cloudName = 'da1u9kl5f';
    const uploadPreset = 'veeflix_media';
    const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`;
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const ext = file.name.split('.').pop();
        const fileName = formatFileName(categorySlug, i + 1, ext);
        
        const formData = new FormData();
        formData.append('file', file.file);
        formData.append('public_id', `${albumSlug}/${fileName}`);
        formData.append('folder', albumSlug);
        formData.append('upload_preset', uploadPreset);
        
        const response = await fetch(uploadUrl, { method: 'POST', body: formData });
        
        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }
        
        const data = await response.json();
        
        uploaded.push({
          id: file.id,
          publicId: data.public_id,
          url: data.secure_url,
          format: data.format,
        });
        
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }
      
      setUploadedUrls(uploaded);
      setStep(4);
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateJSON = () => {
    const mediaItems = files.map((file, index) => {
      const uploaded = uploadedUrls.find(u => u.id === file.id);
      const meta = itemsMeta[file.id] || {};
      const ext = file.name.split('.').pop();
      
      return {
        id: formatFileName(categorySlug, index + 1, ext),
        categoryId: `cat-${categorySlug}`,
        type: file.type,
        title: meta.title || file.title,
        description: meta.description || '',
        imagePath: uploaded ? uploaded.url : '',
        matchPercent: 100,
        year: new Date().getFullYear(),
        rating: 'UA 16+',
        seasonCount: 1,
        badges: [],
        descriptors: {
          cast: categoryMeta.cast.split(',').map(s => s.trim()).filter(Boolean),
          genre: categoryMeta.genre.split(',').map(s => s.trim()).filter(Boolean),
          occasion: categoryMeta.occasion,
          tags: categoryMeta.tags.split(',').map(s => s.trim()).filter(Boolean),
        },
      };
    });
    
    const category = {
      id: `cat-${categorySlug}`,
      title: categoryTitle,
      slug: categorySlug,
      description: categoryDescription,
      cardStyle: 'landscape',
      items: mediaItems.map(m => m.id),
    };
    
    const fullData = {
      client: {
        id: albumSlug,
        name: albumSlug.charAt(0).toUpperCase() + albumSlug.slice(1),
        theme: 'netflix-red',
        logoText: albumSlug.toUpperCase(),
      },
      categories: [category],
      media: mediaItems,
    };
    
    const blob = new Blob([JSON.stringify(fullData, null, 2)], { type: 'application/json' });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `${albumSlug}-${categorySlug}.json`;
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  const handleSkipUpload = () => {
    setSkipUpload(true);
    setStep(4);
  };

  const resetForm = () => {
    setStep(1);
    setAlbumSlug('');
    setCategoryTitle('');
    setCategorySlug('');
    setCategoryDescription('');
    setFiles([]);
    setUploadedUrls([]);
    setUploadProgress(0);
    setSkipUpload(false);
    setCategoryMeta({ cast: '', genre: '', occasion: '', tags: '' });
    setItemsMeta({});
  };

  if (loading) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'grid', placeItems: 'center' }}>
        <p>Loading...</p>
      </main>
    );
  }

  if (!isAuthenticated) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'grid', placeItems: 'center' }}>
        <p style={{ color: '#666' }}>Redirecting to login...</p>
      </main>
    );
  }

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', path: '/admin' },
    { id: 'media', label: 'Media Upload', path: '/admin/media' },
    { id: 'invites', label: 'Invites', path: '/admin/invites' },
    { id: 'users', label: 'Users', path: '/admin/users' },
    { id: 'albums', label: 'Albums', path: '/admin/albums' },
    { id: 'settings', label: 'Settings', path: '/admin/settings' },
  ];

  return (
    <ErrorBoundary>
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff' }}>
        {error && (
          <div style={{ background: '#7f1d1d', padding: '1rem', borderBottom: '1px solid #ef4444', textAlign: 'center' }}>
            <p style={{ color: '#fecaca', margin: 0 }}>⚠️ {error}</p>
          </div>
        )}
        
        <header style={{ background: '#1a1a1a', borderBottom: '1px solid #333', padding: '0 1.5rem' }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Link to="/admin" style={{ textDecoration: 'none' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#e50914' }}>VEEFLIX</span>
              </Link>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Admin</span>
            </div>
            <button onClick={handleLogout} style={{ padding: '0.4rem 0.8rem', borderRadius: 6, border: '1px solid #444', background: 'transparent', color: '#9ca3af', fontSize: '0.8rem', cursor: 'pointer' }}>
              Logout
            </button>
          </div>
        </header>

        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
            {TABS.map(tab => (
              <Link
                key={tab.id}
                to={tab.path}
                style={{
                  padding: '0.5rem 0.9rem',
                  borderRadius: 6,
                  textDecoration: 'none',
                  color: tab.id === 'media' ? '#fff' : '#9ca3af',
                  background: tab.id === 'media' ? '#333' : 'transparent',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {tab.label}
              </Link>
            ))}
          </div>

          <h1 style={{ marginBottom: '1.5rem' }}>Media Upload</h1>
          
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem' }}>
            {[1, 2, 3, 4].map(s => (
              <div
                key={s}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  background: step === s ? '#e50914' : step > s ? '#16a34a' : '#333',
                  color: '#fff',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {s}. {s === 1 && 'Select'}{s === 2 && 'Reorder'}{s === 3 && 'Info'}{s === 4 && 'Done'}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.5rem', marginBottom: '1rem' }}>
              <h2 style={{ marginBottom: '1rem' }}>Step 1: Select Album & Upload Files</h2>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af', fontSize: '0.875rem' }}>Target Album *</label>
                <select 
                  value={albumSlug}
                  onChange={(e) => setAlbumSlug(e.target.value)}
                  style={{ width: '100%', maxWidth: 400, padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }}
                >
                  <option value="">Select album...</option>
                  {ALBUM_OPTIONS.map(p => (
                    <option key={p.slug} value={p.slug}>{p.title}</option>
                  ))}
                </select>
              </div>

              {albumSlug && (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af', fontSize: '0.875rem' }}>Category Title *</label>
                    <input
                      type="text"
                      value={categoryTitle}
                      onChange={(e) => { setCategoryTitle(e.target.value); handleCategorySlugChange(e.target.value); }}
                      placeholder="e.g., The Sush Wedding"
                      style={{ width: '100%', maxWidth: 400, padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af', fontSize: '0.875rem' }}>Category Slug *</label>
                    <input
                      type="text"
                      value={categorySlug}
                      onChange={(e) => setCategorySlug(e.target.value)}
                      style={{ width: '100%', maxWidth: 400, padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }}
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af', fontSize: '0.875rem' }}>Description</label>
                    <input
                      type="text"
                      value={categoryDescription}
                      onChange={(e) => setCategoryDescription(e.target.value)}
                      placeholder="Short description"
                      style={{ width: '100%', maxWidth: 400, padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }}
                    />
                  </div>
                </>
              )}

              {albumSlug && categorySlug && (
                <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af' }}>Select Files *</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                    style={{ width: '100%', padding: '1rem', border: '2px dashed #333', borderRadius: 8, background: '#111', color: '#9ca3af' }}
                  />
                  {files.length > 0 && (
                    <p style={{ marginTop: '0.75rem', color: '#10b981' }}>{files.length} file(s) selected</p>
                  )}
                </div>
              )}

              <button 
                onClick={() => setStep(2)} 
                disabled={!albumSlug || !categorySlug || files.length === 0}
                style={{ 
                  padding: '0.75rem 1.5rem', 
                  background: (!albumSlug || !categorySlug || files.length === 0) ? '#444' : '#16a34a', 
                  color: '#fff', 
                  border: 0, 
                  borderRadius: 6, 
                  fontWeight: 600, 
                  cursor: (!albumSlug || !categorySlug || files.length === 0) ? 'not-allowed' : 'pointer',
                  marginTop: '1rem'
                }}
              >
                Continue to Reorder →
              </button>
            </div>
          )}

          {step === 2 && (
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.5rem', marginBottom: '1rem' }}>
              <h2 style={{ marginBottom: '1rem' }}>Step 2: Reorder Files</h2>
              <p style={{ color: '#9ca3af', marginBottom: '1rem', fontSize: '0.875rem' }}>
                Drag to reorder. Files will be numbered 001, 002, etc.
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
                {files.map((file, index) => (
                  <div
                    key={file.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('text/plain', index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); handleReorder(parseInt(e.dataTransfer.getData('text/plain')), index); }}
                    style={{ background: '#222', borderRadius: 8, padding: '0.5rem', cursor: 'grab', position: 'relative' }}
                  >
                    <div style={{ position: 'absolute', top: 4, left: 4, background: '#e50914', color: '#fff', width: 20, height: 20, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                      {index + 1}
                    </div>
                    {file.preview ? (
                      <img src={file.preview} alt={file.name} style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4, marginTop: '0.25rem' }} />
                    ) : (
                      <div style={{ width: '100%', height: 80, background: '#333', borderRadius: 4, marginTop: '0.25rem', display: 'grid', placeItems: 'center', fontSize: '0.7rem', color: '#666' }}>VIDEO</div>
                    )}
                    <div style={{ fontSize: '0.7rem', marginTop: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name.substring(0, 15)}...</div>
                  </div>
                ))}
              </div>
              
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={() => setStep(1)} style={{ padding: '0.6rem 1.2rem', background: '#333', color: '#fff', border: 0, borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>← Back</button>
                <button onClick={() => setStep(3)} style={{ padding: '0.6rem 1.2rem', background: '#16a34a', color: '#fff', border: 0, borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Continue to Info →</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.5rem', marginBottom: '1rem' }}>
              <h2 style={{ marginBottom: '1rem' }}>Step 3: Add Info</h2>
              <p style={{ color: '#9ca3af', marginBottom: '1rem', fontSize: '0.875rem' }}>
                Add category info (applies to all), then optionally edit individual items.
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af', fontSize: '0.875rem' }}>Cast (comma-separated)</label>
                  <input type="text" value={categoryMeta.cast} onChange={(e) => setCategoryMeta(m => ({ ...m, cast: e.target.value }))} placeholder="Shreya, Suhrit, Family" style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af', fontSize: '0.875rem' }}>Genre (comma-separated)</label>
                  <input type="text" value={categoryMeta.genre} onChange={(e) => setCategoryMeta(m => ({ ...m, genre: e.target.value }))} placeholder="Sweet, Emotional" style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af', fontSize: '0.875rem' }}>Occasion</label>
                  <input type="text" value={categoryMeta.occasion} onChange={(e) => setCategoryMeta(m => ({ ...m, occasion: e.target.value }))} placeholder="Wedding Day" style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af', fontSize: '0.875rem' }}>Tags (comma-separated)</label>
                  <input type="text" value={categoryMeta.tags} onChange={(e) => setCategoryMeta(m => ({ ...m, tags: e.target.value }))} placeholder="Hero, Highlights" style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }} />
                </div>
              </div>

              <details style={{ marginBottom: '1.5rem' }}>
                <summary style={{ cursor: 'pointer', color: '#9ca3af', marginBottom: '0.5rem' }}>Fine-tune individual items (optional)</summary>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', maxHeight: 300, overflowY: 'auto' }}>
                  {files.map((file, index) => (
                    <div key={file.id} style={{ background: '#222', borderRadius: 8, padding: '0.75rem' }}>
                      <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>{index + 1}. {formatFileName(categorySlug, index + 1, file.name.split('.').pop())}</div>
                      <input placeholder="Title" value={itemsMeta[file.id]?.title || ''} onChange={(e) => handleItemMetaChange(file.id, 'title', e.target.value)} style={{ width: '100%', padding: '0.4rem', marginBottom: '0.5rem', borderRadius: 4, border: '1px solid #333', background: '#111', color: '#fff' }} />
                      <input placeholder="Description" value={itemsMeta[file.id]?.description || ''} onChange={(e) => handleItemMetaChange(file.id, 'description', e.target.value)} style={{ width: '100%', padding: '0.4rem', borderRadius: 4, border: '1px solid #333', background: '#111', color: '#fff' }} />
                    </div>
                  ))}
                </div>
              </details>

              {uploading && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ height: 8, background: '#333', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${uploadProgress}%`, background: '#e50914', transition: 'width 300ms' }} />
                  </div>
                  <p style={{ marginTop: '0.5rem', color: '#9ca3af', fontSize: '0.875rem' }}>Uploading {uploadProgress}%...</p>
                </div>
              )}

              {uploadError && <p style={{ color: '#ef4444', marginBottom: '1rem' }}>{uploadError}</p>}
              
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button onClick={() => setStep(2)} style={{ padding: '0.6rem 1.2rem', background: '#333', color: '#fff', border: 0, borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>← Back</button>
                <button onClick={handleSkipUpload} style={{ padding: '0.6rem 1.2rem', background: '#6b7280', color: '#fff', border: 0, borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Skip Upload (manual URLs)</button>
                <button onClick={handleUploadToCloudinary} disabled={uploading} style={{ padding: '0.6rem 1.2rem', background: uploading ? '#444' : '#e50914', color: '#fff', border: 0, borderRadius: 6, fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer' }}>
                  {uploading ? `Uploading ${uploadProgress}%` : 'Upload to Cloudinary'}
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.5rem', marginBottom: '1rem' }}>
              <h2 style={{ marginBottom: '1rem', color: skipUpload ? '#f59e0b' : '#10b981' }}>
                {skipUpload ? '⚠ Manual URLs required' : `✓ Successfully uploaded ${uploadedUrls.length} file(s)!`}
              </h2>
              <p style={{ color: '#9ca3af', marginBottom: '1rem', fontSize: '0.875rem' }}>
                {skipUpload 
                  ? 'Upload files to Cloudinary manually, then add URLs in the JSON. Download and edit the imagePath fields.' 
                  : 'Files are in Cloudinary. Download the JSON and add to your project.'}
              </p>
              
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <button onClick={handleGenerateJSON} style={{ padding: '0.75rem 1.5rem', background: '#16a34a', color: '#fff', border: 0, borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Download JSON</button>
                <button onClick={resetForm} style={{ padding: '0.75rem 1.5rem', background: '#333', color: '#fff', border: 0, borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Upload More</button>
              </div>

              <details>
                <summary style={{ cursor: 'pointer', color: '#9ca3af' }}>View uploaded file IDs</summary>
                <ul style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#666', maxHeight: 200, overflowY: 'auto' }}>
                  {uploadedUrls.map((url, i) => (
                    <li key={i} style={{ marginBottom: '0.25rem', wordBreak: 'break-all' }}>{url.publicId}</li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      </main>
    </ErrorBoundary>
  );
}

export default AdminMediaUpload;
